import * as THREE from 'three';
import { buildSkyMaterial } from '../assets/Shaders';
import { buildOceanMaterial } from '../assets/Shaders';
import { Boat, DEFAULT_BOAT_TUNING, sampleWave } from '../entities/Boat';
import { RivalBoat, createRivalField } from '../entities/RivalBoat';
import { BoostPod, BuoyField, Gate } from '../entities/CourseActors';
import { createBuoy, createCrateStack, createDistantIsland, createDock, createIsland, createLighthouse } from '../assets/modelFactories/WorldPropKit';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { createGodRaysPass, createGradePass, projectSunToScreen } from '../systems/PostFX';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { DebugTools } from '../systems/DebugTools';
import { Hud } from '../systems/Hud';
import { VfxSystem } from '../systems/VfxSystem';
import { NextGatePointer } from '../systems/NextGatePointer';
import { Minimap } from '../systems/Minimap';
import { createSeededRandom } from '../utils/random';

const TOTAL_LAPS = 3;
const RIVAL_START_SLOTS: Array<{ x: number; z: number }> = [
  { x: 8, z: -58 },
  { x: -9, z: -57 },
  { x: 4, z: -62 },
];
const GATE_LAYOUT: Array<[number, number, number]> = [
  // x, z, heading (radians; gate faces along course direction)
  [0, -46, 0],
  [30, -34, Math.PI * 0.28],
  [44, -2, Math.PI * 0.52],
  [34, 32, -Math.PI * 0.28],
  [0, 46, Math.PI],
  [-34, 34, Math.PI + Math.PI * 0.28],
  [-46, 0, -Math.PI * 0.52],
  [-32, -34, Math.PI * 0.78],
];


export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(52, 1, 0.5, 1600);
  private readonly input: InputController;
  private readonly boat = new Boat(DEFAULT_BOAT_TUNING);
  private readonly gates: Gate[] = [];
  private readonly boostPods: BoostPod[] = [];
  private readonly buoys = new BuoyField();
  private readonly islands: Array<{ pos: THREE.Vector2; radius: number }> = [];
  private readonly vfx: VfxSystem;
  private readonly gatePointer = new NextGatePointer();
  private readonly minimap = new Minimap('#minimap', 190);
  private readonly audio = new AudioSystem();
  private readonly hud = new Hud();
  private readonly cameraRig: CameraRig;
  private readonly sky: THREE.Mesh;
  private readonly ocean: THREE.Mesh;
  private readonly composer: EffectComposer;
  private readonly bloomPass: UnrealBloomPass;
  private readonly godRaysPass: ReturnType<typeof createGodRaysPass>;
  private readonly gradePass: ReturnType<typeof createGradePass>;
  private readonly sunWorldPos = new THREE.Vector3(0.42, 0.13, 0.9).normalize().multiplyScalar(900);
  private readonly sunScreenUv = new THREE.Vector2(0.5, 0.7);
  private readonly oceanMaterial: THREE.ShaderMaterial;
  private readonly skyMaterial: THREE.ShaderMaterial;
  private readonly loop = new Loop(
    (delta) => this.update(delta),
    () => this.render(),
  );
  private readonly debugTools: DebugTools;

  private readonly rivals: RivalBoat[] = [];
  private playerPosition = 1;
  private raceStarted = false;
  private countdown = 3.999;
  private driftCharge = 0;
  private airborne = false;
  private airTime = 0;
  private trickRoll = 0; // -1..1 signed roll direction while airborne
  private trickRollAngle = 0; // degrees accumulated
  private frame = 0;
  private gameClock = 0;
  private shakeTime = 0;
  private lap = 1;
  private currentGate = 0;
  private finished = false;
  private hitstop = 0;
  private timeScale = 1;
  private splashCooldown = 0;
  private rng = createSeededRandom(1);
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private readonly move = new THREE.Vector2();

  private readonly tuning = {
    cameraLag: 0.14,
    exposure: 1.0,
    maxDpr: 2,
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;

    const stick = this.requireElement('#touch-stick');
    const knob = this.requireElement('#touch-knob');
    const dashButton = this.requireElement('#dash-button');
    this.input = new InputController(stick, knob, dashButton);

    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    });

    this.vfx = new VfxSystem(this.scene);

    // Environment map for PBR: bake a dedicated sky dome instance.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const envScene = new THREE.Scene();
    envScene.add(new THREE.Mesh(new THREE.SphereGeometry(100, 24, 12), buildSkyMaterial()));
    this.scene.environment = pmrem.fromScene(envScene, 0.04).texture;
    pmrem.dispose();

    this.skyMaterial = buildSkyMaterial();
    this.sky = new THREE.Mesh(new THREE.SphereGeometry(1000, 32, 16), this.skyMaterial);
    this.sky.frustumCulled = false;
    this.scene.add(this.sky);

    // Ocean
    this.oceanMaterial = buildOceanMaterial();
    // 2u quads on desktop: the shortest Gerstner wave (5u) needs ~2.5 samples
    // per crest or the surface aliases. Mobile tier is 300k tris total, so the
    // ocean drops to 256x256 (~4u quads — still resolves every wave >= 5u at
    // 2 samples+) and the world fits the budget.
    const coarse = window.matchMedia('(max-width: 760px), (pointer: coarse)').matches;
    const segments = coarse ? 256 : 400;
    this.ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(800, 800, segments, segments),
      this.oceanMaterial,
    );
    this.ocean.rotation.x = -Math.PI / 2;
    this.ocean.position.y = -0.35;
    this.scene.add(this.ocean);

    this.buildLighting();
    this.buildWorld();
    this.buildCourse();

    this.scene.add(this.boat.group);
    this.scene.add(this.gatePointer.group);
    this.boat.snapTo(0, -60, 0);
    const gatePath = GATE_LAYOUT.map(([x, z]) => ({ x, z }));
    this.rivals.push(...createRivalField(gatePath));
    this.rivals.forEach((rival, i) => {
      rival.group.position.set(RIVAL_START_SLOTS[i].x, 0, RIVAL_START_SLOTS[i].z);
    });
    for (const rival of this.rivals) this.scene.add(rival.group);

    this.cameraRig = new CameraRig(this.camera);
    this.cameraRig.snapTo(this.boat.group.position, 0);

    this.hud.setTotals(TOTAL_LAPS, GATE_LAYOUT.length);

    // Post: sunset bloom on emissive gates/flames/glitter + subtle vignette.
    // Cap post-processing pixel ratio: bloom at DPR 2 is 4x fill rate — too heavy
    // for mobile/low-end; 1.25 keeps the glow readable at a fraction of the cost.
    const composerDpr = Math.min(window.devicePixelRatio || 1, 1.25);
    this.composer = new EffectComposer(this.renderer);
    this.composer.setPixelRatio(composerDpr);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(canvas.clientWidth, canvas.clientHeight),
      0.38, 0.3, 0.95,
    );
    this.godRaysPass = createGodRaysPass();
    this.composer.addPass(this.godRaysPass);
    this.composer.addPass(this.bloomPass);
    // Final grade: grade + chromatic aberration + grain + speed streaks + vignette.
    this.gradePass = createGradePass();
    this.composer.addPass(this.gradePass);
    this.composer.addPass(new OutputPass());

    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.composer.setSize(canvas.clientWidth, canvas.clientHeight);
    this.installTestHooks();
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  beginRace(): void {
    this.resetRun();
  }

  async resumeAudio(): Promise<void> {
    await this.audio.unlock();
  }

  toggleMute(): boolean {
    this.audio.setMuted(!this.audio.muted);
    return this.audio.muted;
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.vfx.dispose();
    this.gatePointer.dispose();
    this.boat.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private update(delta: number): void {
    this.frame += 1;
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }

    if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr)) {
      this.composer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
    }

    // Hitstop: freeze gameplay clock, keep feedback alive.
    if (this.hitstop > 0) {
      this.hitstop -= delta;
      if (this.hitstop <= 0) this.timeScale = 1;
    }
    const gameplayDelta = delta * this.timeScale;
    const animDelta = this.reducedMotion ? 0 : gameplayDelta;
    if (!this.finished) this.gameClock += gameplayDelta;

    this.input.readMovement(this.move);

    if (!this.raceStarted) {
      this.countdown -= delta;
      this.hud.setCountdown(this.countdown);
      if (this.countdown <= 0) {
        this.raceStarted = true;
        this.hud.setCountdown(0);
        this.audio.lap();
      }
    }

    // Title-screen cinematic: orbit the bay at golden hour until START.
    // The chase rig must not fight the sweep — skip all gameplay-camera work here.
    const titleVisible = !document.querySelector('#title-screen')?.classList.contains('hidden');
    if (titleVisible && !this.raceStarted) {
      this.introSweep(delta, this.gameClock);
      this.oceanMaterial.uniforms.uTime.value = this.gameClock;
      this.oceanMaterial.uniforms.uTimeF.value = this.gameClock;
      this.oceanMaterial.uniforms.uCamPos.value.copy(this.camera.position);
      this.skyMaterial.uniforms.uCloudOffset.value.set(this.gameClock * 0.004, 0);
      this.sky.position.set(this.camera.position.x, 0, this.camera.position.z);
      // HUD must not bleed through the title: hide the entire HUD cluster.
      document.querySelector('#hud')?.setAttribute('style', 'display: none');
      document.querySelector('#event-banner')?.setAttribute('style', 'display: none');
      document.querySelector('#countdown-value')?.setAttribute('style', 'opacity: 0');
      document.querySelector('#drift-meter')?.setAttribute('style', 'display: none');
      document.querySelector('#minimap')?.setAttribute('style', 'display: none');
      this.publishDiagnostics();
      return;
    }
    // Racing: restore HUD visibility (idempotent).
    document.querySelector('#hud')?.removeAttribute('style');
    document.querySelector('#event-banner')?.removeAttribute('style');
    document.querySelector('#countdown-value')?.removeAttribute('style');
    document.querySelector('#drift-meter')?.removeAttribute('style');
    document.querySelector('#minimap')?.removeAttribute('style');

    const controlsLive = this.raceStarted && !this.finished;
    const boostHeld = this.input.isDashHeld() && this.boat.boost > 0.02 && this.boat.speed > 2;
    this.boat.update(gameplayDelta, this.gameClock, this.move, boostHeld && controlsLive);

    const turning = Math.abs(this.move.x) > 0.5;
    if (controlsLive && turning && this.boat.speed > 12) {
      this.driftCharge = Math.min(1, this.driftCharge + gameplayDelta * 0.45);
    } else if (this.driftCharge >= 1 && this.input.isDashHeld()) {
      this.boat.boost = 1;
      this.driftCharge = 0;
      this.hud.bannerFlash('DRIFT BOOST!', '#22d3ee');
      this.cameraRig.punchFov(6);
      this.audio.boostPickup();
    } else {
      this.driftCharge = Math.max(0, this.driftCharge - gameplayDelta * 0.3);
    }
    this.hud.setDrift(this.driftCharge);

    if (controlsLive) {
      // Vertical water velocity from wave slope: riding up a face at speed = launch.
      const wave = sampleWave(this.boat.group.position.x, this.boat.group.position.z, this.gameClock);
      const vy = wave.slopeX * this.boat.velocity.x + wave.slopeZ * this.boat.velocity.y;
      if (!this.airborne && vy > 1.6 && this.boat.speed > 13) {
        this.airborne = true;
        this.airTime = 0;
      }
      if (this.airborne) {
        this.airTime += gameplayDelta;
        // Barrel roll: steer while airborne; full 360 = clean roll
        if (Math.abs(this.move.x) > 0.4) {
          this.trickRoll = Math.sign(this.move.x);
          this.trickRollAngle += Math.abs(this.move.x) * gameplayDelta * 520;
          this.boat.group.rotation.z = -this.trickRoll * (this.trickRollAngle * Math.PI / 180);
        }
        if (vy < 0.2 || this.airTime > 1.4) {
          this.airborne = false;
          if (this.airTime > 0.15) {
            this.vfx.emit({ type: 'splash', position: this.boat.group.position.clone().setY(0.2), strength: Math.min(1, this.airTime * 2) });
            this.cameraRig.addTrauma(Math.min(0.4, this.airTime));
            this.audio.collision(0.25);
            if (this.trickRollAngle > 320) {
              // Clean barrel roll: reward nitro + speed
              this.boat.boost = Math.min(1, this.boat.boost + 0.45);
              this.boat.velocity.multiplyScalar(1.28);
              this.hud.bannerFlash('BARREL ROLL!', '#ffcc33');
              this.audio.gate();
              this.cameraRig.punchFov(7);
            } else if (this.airTime > 0.45) {
              this.hud.bannerFlash('AIR TIME!', '#22d3ee');
            }
          }
          this.trickRoll = 0;
          this.trickRollAngle = 0;
          // Boat re-levels its roll via Boat.update's existing smoothing
        }
      }
    }

    if (this.raceStarted && !this.finished) {
      const playerProgress = this.playerProgressValue();
      for (const rival of this.rivals) {
        rival.update(gameplayDelta, this.gameClock, playerProgress, this.boat.speed);
      }
      this.updatePlayerPosition();
    }

    this.gatePointer.update(
      this.boat.group.position,
      this.boat.headingAngle,
      this.gates,
      this.currentGate,
      this.gameClock,
      this.finished,
    );
    this.minimap.update(
      this.boat.group.position.x,
      this.boat.group.position.z,
      this.boat.headingAngle,
      this.gates,
      this.currentGate,
      this.rivals,
      this.finished,
    );

    // Ocean shader clock + wake tracking
    this.oceanMaterial.uniforms.uTime.value = this.gameClock;
    this.oceanMaterial.uniforms.uTimeF.value = this.gameClock;
    this.oceanMaterial.uniforms.uBoatX.value = this.boat.group.position.x;
    this.oceanMaterial.uniforms.uBoatZ.value = this.boat.group.position.z;
    this.oceanMaterial.uniforms.uWakeStrength.value = Math.min(1, this.boat.speed / 18);
    this.oceanMaterial.uniforms.uCamPos.value.copy(this.camera.position);
    this.skyMaterial.uniforms.uCloudOffset.value.set(this.gameClock * 0.004, 0);

    // God rays track the projected sun; grade reacts to speed.
    projectSunToScreen(this.camera, this.sunWorldPos, this.sunScreenUv);
    this.godRaysPass.uniforms.uSunScreenPos.value.copy(this.sunScreenUv);
    const speedNorm = Math.min(1, this.boat.speed / DEFAULT_BOAT_TUNING.boostSpeed);
    this.gradePass.uniforms.uSpeed.value = speedNorm;
    this.gradePass.uniforms.uTime.value = this.gameClock;

    // Sky follows camera so the dome never clips.
    this.sky.position.set(this.camera.position.x, 0, this.camera.position.z);

    // Shadow frustum tracks the boat so the shadow map stays dense.
    this.sunLight.position.set(
      this.boat.group.position.x + 120,
      160,
      this.boat.group.position.z + 180,
    );
    this.sunLight.target.position.set(this.boat.group.position.x, 0, this.boat.group.position.z);
    this.sunLight.target.updateMatrixWorld();

    // Wake + spray curtains: stern wake always at speed; side curtains when carving.
    this.splashCooldown -= delta;
    if (this.splashCooldown <= 0 && this.boat.speed > 8 && !this.finished) {
      this.splashCooldown = 0.08;
      const behind = new THREE.Vector3(
        this.boat.group.position.x - Math.sin(this.boat.headingAngle) * 2.4,
        0.1,
        this.boat.group.position.z - Math.cos(this.boat.headingAngle) * 2.4,
      );
      this.vfx.emit({ type: 'splash', position: behind, strength: Math.min(1, this.boat.speed / 26) });
      // Carve spray off both bow quarters when steering hard at speed
      if (Math.abs(this.move.x) > 0.4 && this.boat.speed > 14) {
        for (const side of [-1, 1]) {
          const off = new THREE.Vector3(
            this.boat.group.position.x
              - Math.sin(this.boat.headingAngle) * 1.4
              + Math.cos(this.boat.headingAngle) * side * 1.1,
            0.15,
            this.boat.group.position.z
              - Math.cos(this.boat.headingAngle) * 1.4
              - Math.sin(this.boat.headingAngle) * side * 1.1,
          );
          this.vfx.emit({ type: 'splash', position: off, strength: 0.5 + Math.abs(this.move.x) * 0.4 });
        }
      }
    }

    // Boost trail
    if (this.boat.boostActive) {
      const nozzle = new THREE.Vector3(
        this.boat.group.position.x - Math.sin(this.boat.headingAngle) * 2.6,
        0.5,
        this.boat.group.position.z - Math.cos(this.boat.headingAngle) * 2.6,
      );
      this.vfx.emit({ type: 'boost', position: nozzle });
    }

    this.checkGates();
    this.checkBoostPods();
    this.checkCollisions();
    this.keepOnCourse();

    for (const gate of this.gates) gate.update(animDelta, this.gameClock, this.gates.indexOf(gate) === this.currentGate && !this.finished);
    for (const pod of this.boostPods) pod.update(animDelta, this.gameClock);
    this.buoys.update(this.gameClock);

    this.vfx.update(delta);
    this.audio.updateEngine(Math.min(1, this.boat.speed / DEFAULT_BOAT_TUNING.maxSpeed), this.boat.boostActive);

    this.shakeTime += delta;
    const speed01 = Math.min(1, this.boat.speed / DEFAULT_BOAT_TUNING.maxSpeed);
    this.cameraRig.update(delta, this.boat.group.position, this.boat.headingAngle, speed01, this.boat.boostActive, this.shakeTime);

    this.hud.update({
      position: this.playerPosition,
      speed: this.boat.speed,
      maxSpeed: DEFAULT_BOAT_TUNING.boostSpeed,
      nitro: this.boat.boost,
      lap: this.lap,
      gate: this.currentGate,
      gates: GATE_LAYOUT.length,
      elapsed: this.gameClock,
      finished: this.finished,
    });
    this.publishDiagnostics();
  }

  private render(): void {
    this.composer.render();
  }

  private checkGates(): void {
    if (this.finished || this.currentGate >= GATE_LAYOUT.length) return;
    const gate = this.gates[this.currentGate];
    const dx = this.boat.group.position.x - gate.position.x;
    const dz = this.boat.group.position.z - gate.position.y;
    if (dx * dx + dz * dz < gate.radius * gate.radius) {
      const previous = this.currentGate;
      this.currentGate += 1;
      gate.markPassed();
      this.audio.gate();
      this.vfx.emit({ type: 'gate', position: gate.group.position.clone().setY(1) });
      this.cameraRig.addTrauma(0.12);
      if (previous === GATE_LAYOUT.length - 1) {
        // Lap complete
        this.lap += 1;
        if (this.lap > TOTAL_LAPS) {
          this.finished = true;
          this.lap = TOTAL_LAPS;
          this.audio.finish();
          const place = ['1ST', '2ND', '3RD', '4TH'][Math.min(this.playerPosition - 1, 3)];
          this.hud.bannerFlash(`${place} PLACE — ${Math.floor(this.gameClock)}s`, this.playerPosition === 1 ? '#ffcc33' : '#7dffb3');
          Hud.setBestTime(this.gameClock);
          this.showPodium();
        } else {
          this.currentGate = 0;
          this.audio.lap();
          this.hud.bannerFlash(`LAP ${this.lap}/${TOTAL_LAPS}`, '#ffcc33');
          this.vfx.emit({ type: 'lap', position: this.boat.group.position.clone().setY(1) });
          this.cameraRig.addTrauma(0.25);
        }
      } else {
        this.hud.bannerFlash(`GATE ${this.currentGate}/${GATE_LAYOUT.length}`, '#7dffb3');
      }
    }
  }

  private checkBoostPods(): void {
    for (const pod of this.boostPods) {
      if (!pod.active) continue;
      const dx = this.boat.group.position.x - pod.position.x;
      const dz = this.boat.group.position.z - pod.position.y;
      if (dx * dx + dz * dz < 2.4 * 2.4) {
        pod.collect();
        this.boat.boost = 1;
        this.audio.boostPickup();
        this.vfx.emit({ type: 'boostPickup', position: pod.group.position.clone() });
        this.hud.bannerFlash('NITRO FULL', '#22d3ee');
        this.cameraRig.punchFov(4);
      }
    }
  }

  private checkCollisions(): void {
    // Islands: push boat out + trauma + audio thud.
    for (const island of this.islands) {
      const dx = this.boat.group.position.x - island.pos.x;
      const dz = this.boat.group.position.z - island.pos.y;
      const dist = Math.hypot(dx, dz);
      const minDist = island.radius * 0.92 + this.boat.model.collisionRadius * 0.4;
      if (dist < minDist) {
        const nx = dx / (dist || 1);
        const nz = dz / (dist || 1);
        this.boat.group.position.x = island.pos.x + nx * minDist;
        this.boat.group.position.z = island.pos.y + nz * minDist;
        const intoIsland = -(this.boat.velocity.x * nx + this.boat.velocity.y * nz);
        if (intoIsland > 2) {
          this.boat.velocity.multiplyScalar(0.35);
          this.cameraRig.addTrauma(Math.min(0.6, intoIsland * 0.02));
          this.audio.collision(Math.min(1, intoIsland / 18));
          this.hitstop = Math.max(this.hitstop, 0.06);
          this.timeScale = 0.15;
        }
      }
    }
    // Boat-vs-boat: radial separation so hulls never ghost through each other.
    for (const rival of this.rivals) {
      const dx = this.boat.group.position.x - rival.position.x;
      const dz = this.boat.group.position.z - rival.position.z;
      const dist = Math.hypot(dx, dz);
      const minDist = 3.4;
      if (dist < minDist && dist > 0.001) {
        const nx = dx / dist;
        const nz = dz / dist;
        const push = (minDist - dist) * 0.5;
        this.boat.group.position.x += nx * push;
        this.boat.group.position.z += nz * push;
        rival.position.x -= nx * push;
        rival.position.z -= nz * push;
        const closing = -(this.boat.velocity.x * nx + this.boat.velocity.y * nz);
        if (closing > 3) {
          this.boat.velocity.multiplyScalar(0.92);
          this.cameraRig.addTrauma(0.08);
          this.audio.collision(0.2);
        }
      }
    }

    // Buoys: soft bump.
    for (let i = 0; i < this.buoys.positions.length; i++) {
      const b = this.buoys.positions[i];
      const dx = this.boat.group.position.x - b.x;
      const dz = this.boat.group.position.z - b.y;
      const dist = Math.hypot(dx, dz);
      if (dist < 1.6) {
        const nx = dx / (dist || 1);
        const nz = dz / (dist || 1);
        this.boat.group.position.x = b.x + nx * 1.6;
        this.boat.group.position.z = b.y + nz * 1.6;
        this.boat.velocity.multiplyScalar(0.9);
        this.cameraRig.addTrauma(0.06);
      }
    }
  }

  private keepOnCourse(): void {
    // Soft world boundary: 190 radius.
    const d = Math.hypot(this.boat.group.position.x, this.boat.group.position.z);
    if (d > 190) {
      const k = 190 / d;
      this.boat.group.position.x *= k;
      this.boat.group.position.z *= k;
      this.boat.velocity.multiplyScalar(0.6);
    }
  }

  private buildLighting(): void {
    const sun = new THREE.DirectionalLight('#fff3d0', 2.4);
    sun.position.set(120, 160, 180);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 20;
    sun.shadow.camera.far = 600;
    sun.shadow.camera.left = -70;
    sun.shadow.camera.right = 70;
    sun.shadow.camera.top = 70;
    sun.shadow.camera.bottom = -70;
    sun.shadow.bias = -0.0004;
    this.scene.add(sun);
    // Shadow follows the boat.
    this.sunLight = sun;
    this.scene.add(sun.target);

    const hemi = new THREE.HemisphereLight('#cfe8ff', '#0a3550', 0.75);
    this.scene.add(hemi);
  }

  private sunLight!: THREE.DirectionalLight;

  private buildWorld(): void {
    // Central island cluster (inside course ring)
    const center = createIsland(26, 10, 7);
    center.position.set(0, 0, 0);
    this.scene.add(center);
    this.islands.push({ pos: new THREE.Vector2(0, 0), radius: 26 });

    const lighthouse = createLighthouse();
    lighthouse.position.set(14, 8.5, 6);
    this.scene.add(lighthouse);

    const dock = createDock();
    dock.position.set(-12, 0.4, 18);
    dock.rotation.y = 0.5;
    this.scene.add(dock);

    // Outer scatter islands (outside the racing ring)
    const outerSpots: Array<[number, number, number]> = [
      [110, 60, 18], [-100, 70, 14], [130, -80, 22], [-120, -60, 16],
      [60, 130, 12], [-70, -125, 15],
    ];
    for (const [x, z, r] of outerSpots) {
      const island = createIsland(r, Math.max(2, Math.round(r / 4)), x * 0.7 + z);
      island.position.set(x, 0, z);
      this.scene.add(island);
      this.islands.push({ pos: new THREE.Vector2(x, z), radius: r });
    }

    // Distant silhouettes
    const farColors = ['#4a7fa8', '#5d8fb0', '#3d6f96'];
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2 + 0.35;
      const r = 560 + (i % 3) * 90;
      const island = createDistantIsland(70 + (i % 4) * 26, new THREE.Color(farColors[i % 3]));
      island.position.set(Math.cos(a) * r, -4, Math.sin(a) * r);
      this.scene.add(island);
    }

    // Crate hazards on the course
    const crateSpots: Array<[number, number]> = [[18, -12], [-16, -18], [24, 22], [-22, 14], [2, -28], [-38, -20]];
    for (let i = 0; i < crateSpots.length; i++) {
      const [x, z] = crateSpots[i];
      const stack = createCrateStack(i * 7 + 3);
      stack.position.set(x, 0, z);
      stack.rotation.y = this.rng() * Math.PI;
      this.scene.add(stack);
      this.islands.push({ pos: new THREE.Vector2(x, z), radius: 1.4 });
    }
  }

  private buildCourse(): void {
    GATE_LAYOUT.forEach(([x, z, heading], i) => {
      const gate = new Gate(i, x, z, 7, heading);
      this.gates.push(gate);
      this.scene.add(gate.group);
    });

    // Boost pods between gates
    const podSpots: Array<[number, number]> = [
      [15, -42], [38, -20], [40, 16], [18, 40], [-2, 50, ], [-30, 40], [-44, 12], [-40, -22],
    ];
    podSpots.forEach(([x, z], i) => {
      const pod = new BoostPod(i, x, z);
      this.boostPods.push(pod);
      this.scene.add(pod.group);
    });

    // Buoy line marking the course corridor + hazards
    const buoySpots: Array<[number, number]> = [
      [12, -52], [-12, -52], [52, -30], [58, 10], [36, 48], [-36, 50], [-58, 14], [-52, -26],
      [8, -22], [-8, -22], [30, 6], [-30, 8], [0, 30], [0, -30],
    ];
    for (const [x, z] of buoySpots) {
      this.buoys.add(x, z, () => {
        const buoy = createBuoy();
        return buoy;
      });
      this.scene.add(this.buoys.groups[this.buoys.groups.length - 1]);
    }
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (name === 'active-play') {
          this.resetRun();
          // Testing: jump straight past countdown so captures show racing
          this.raceStarted = true;
          this.countdown = 0;
          this.hud.setCountdown(0);
        } else if (name === 'countdown') {
          this.resetRun();
        } else if (name === 'podium') {
          this.resetRun();
          this.raceStarted = true;
          this.lap = TOTAL_LAPS;
          this.currentGate = GATE_LAYOUT.length - 1;
          this.finished = false;
          // simulate crossing final gate
          for (let i = 0; i < GATE_LAYOUT.length - 1; i++) this.gates[i].markPassed();
          this.gateFinishForTest();
        }
        else if (name === 'active-boost') {
          this.resetRun();
          // Position mid-course with speed for action screenshots
          this.boat.snapTo(30, -30, Math.PI * 0.3);
          this.boat.velocity.set(14, 10);
        } else if (name === 'finish') {
          this.finished = true;
          this.gameClock = 142.5;
        } else console.warn(`Unknown test state: ${name}`);
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
    };
  }

  private resetRun(): void {
    document.querySelector('#podium-screen')?.classList.remove('active');
    this.lap = 1;
    this.currentGate = 0;
    this.finished = false;
    this.gameClock = 0;
    this.hitstop = 0;
    this.timeScale = 1;
    this.countdown = 3.999;
    this.raceStarted = false;
    this.playerPosition = 1;
    this.driftCharge = 0;
    this.airborne = false;
    this.hud.setCountdown(this.countdown);
    for (const gate of this.gates) gate.reset();
    for (const pod of this.boostPods) pod.reset();
    this.rivals.forEach((rival, i) => {
      rival.gateIndex = 0;
      rival.lap = 1;
      rival.group.position.set(RIVAL_START_SLOTS[i].x, 0, RIVAL_START_SLOTS[i].z);
    });
    this.boat.snapTo(0, -60, 0);
    this.cameraRig.snapTo(this.boat.group.position, 0);
  }

  private gateFinishForTest(): void {
    this.finished = true;
    this.playerPosition = 2;
    this.showPodium();
    this.hud.update({
      position: this.playerPosition,
      speed: 0,
      maxSpeed: DEFAULT_BOAT_TUNING.boostSpeed,
      nitro: this.boat.boost,
      lap: TOTAL_LAPS,
      gate: GATE_LAYOUT.length,
      gates: GATE_LAYOUT.length,
      elapsed: this.gameClock,
      finished: true,
    });
  }

  private showPodium(): void {
    const podium = document.querySelector<HTMLElement>('#podium-screen');
    const standings = document.querySelector<HTMLElement>('#standings');
    if (!podium || !standings) return;
    const entries: Array<{ name: string; progress: number; player: boolean }> = [
      { name: 'YOU', progress: this.playerProgressValue() + 0.5, player: true },
      ...this.rivals.map((r) => ({ name: r.name, progress: r.progress, player: false })),
    ];
    entries.sort((a, b) => b.progress - a.progress);
    standings.innerHTML = '';
    entries.forEach((entry, i) => {
      const row = document.createElement('div');
      row.className = `standing-row${i === 0 ? ' first' : ''}${entry.player ? ' player' : ''}`;
      row.innerHTML = `<span class="standing-place">${i + 1}</span><span class="standing-name">${entry.name}</span>`;
      standings.appendChild(row);
    });
    podium.classList.add('active');
  }

  // Title-screen cinematic: slow orbit of the bay with the hero boat parked
  // at a scenic spot (not an empty bay) and the countdown HUD hidden.
  private introSweep(delta: number, elapsed: number): void {
    const t = elapsed * 0.11;
    const radius = 46;
    this.camera.position.set(
      Math.cos(t) * radius,
      9.5 + Math.sin(t * 0.7) * 2.5,
      Math.sin(t) * radius,
    );
    this.camera.lookAt(0, 2.0, 0);
    // Hero boat crossing the bay frame: circles slowly so every orbit angle
    // shows it somewhere readable in the composition.
    const boatT = elapsed * 0.05;
    this.boat.group.position.set(Math.cos(boatT) * 26, 0, Math.sin(boatT) * 26);
    this.boat.group.rotation.y = boatT + Math.PI * 0.5;
    void delta;
  }

  playerProgressValue(): number {
    return GATE_LAYOUT.length * (this.lap - 1) + this.currentGate;
  }

  private updatePlayerPosition(): void {
    const mine = this.playerProgressValue();
    let ahead = 0;
    for (const rival of this.rivals) {
      if (rival.progress > mine) ahead += 1; // finished rivals still rank ahead
    }
    const newPosition = 1 + ahead;
    if (newPosition !== this.playerPosition) {
      const gained = newPosition < this.playerPosition;
      this.playerPosition = newPosition;
      const suffix = ['st', 'nd', 'rd', 'th'][Math.min(newPosition - 1, 3)];
      this.hud.bannerFlash(`${newPosition}${suffix} PLACE`, gained ? '#ffcc33' : '#eaf6ff');
      if (gained) this.audio.gate();
    }
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.gameClock,
      lap: this.lap,
      gate: this.currentGate,
      position: this.playerPosition,
      raceStarted: this.raceStarted,
      countdown: this.countdown,
      finished: this.finished,
      boat: {
        x: this.boat.group.position.x,
        z: this.boat.group.position.z,
        speed: this.boat.speed,
        heading: this.boat.headingAngle,
        nitro: this.boat.boost,
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }

  private requireElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
